/**
 * Cookie Consent Hook
 * Manages user cookie preferences with localStorage persistence
 */

import { useState, useEffect, useCallback } from 'react';
import logger from '../utils/logger';

// Consent levels
export const CONSENT_LEVELS = {
  NONE: 'none', // No consent given, show banner
  ESSENTIAL: 'essential', // Only essential cookies
  ALL: 'all', // All cookies accepted
};

// Storage key for consent data
const CONSENT_STORAGE_KEY = 'trona_cookie_consent';

// Consent data version (increment when cookie policy changes)
const CONSENT_VERSION = '1.0';

/**
 * Get stored consent data from localStorage
 * @returns {Object|null} Stored consent data or null
 */
const getStoredConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored);

    // Check if consent version matches
    if (data.version !== CONSENT_VERSION) {
      // Version mismatch - require new consent
      return null;
    }

    return data;
  } catch (error) {
    logger.error('Error reading cookie consent:', error);
    return null;
  }
};

/**
 * Store consent data in localStorage
 * @param {string} level - Consent level
 */
const storeConsent = (level) => {
  try {
    const data = {
      level,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    logger.error('Error storing cookie consent:', error);
  }
};

/**
 * Remove consent data from localStorage
 */
const clearConsent = () => {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch (error) {
    logger.error('Error clearing cookie consent:', error);
  }
};

/**
 * Hook for managing cookie consent
 * @returns {Object} Consent state and actions
 */
export const useCookieConsent = () => {
  const [consentLevel, setConsentLevel] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showBanner, setShowBanner] = useState(false);

  // Initialize consent state from storage
  useEffect(() => {
    const stored = getStoredConsent();

    if (stored) {
      setConsentLevel(stored.level);
      setShowBanner(false);
    } else {
      setConsentLevel(CONSENT_LEVELS.NONE);
      setShowBanner(true);
    }

    setIsLoading(false);
  }, []);

  /**
   * Accept all cookies
   */
  const acceptAll = useCallback(() => {
    storeConsent(CONSENT_LEVELS.ALL);
    setConsentLevel(CONSENT_LEVELS.ALL);
    setShowBanner(false);
  }, []);

  /**
   * Accept only essential cookies
   */
  const acceptEssential = useCallback(() => {
    storeConsent(CONSENT_LEVELS.ESSENTIAL);
    setConsentLevel(CONSENT_LEVELS.ESSENTIAL);
    setShowBanner(false);
  }, []);

  /**
   * Reject all non-essential cookies (same as essential only)
   */
  const rejectAll = useCallback(() => {
    storeConsent(CONSENT_LEVELS.ESSENTIAL);
    setConsentLevel(CONSENT_LEVELS.ESSENTIAL);
    setShowBanner(false);
  }, []);

  /**
   * Reset consent (show banner again)
   */
  const resetConsent = useCallback(() => {
    clearConsent();
    setConsentLevel(CONSENT_LEVELS.NONE);
    setShowBanner(true);
  }, []);

  /**
   * Check if a specific cookie type is allowed
   * @param {string} type - Cookie type (essential, functional, analytics, marketing)
   * @returns {boolean} Whether the cookie type is allowed
   */
  const isCookieAllowed = useCallback(
    (type) => {
      // Essential cookies are always allowed
      if (type === 'essential') return true;

      // If no consent yet, only essential allowed
      if (!consentLevel || consentLevel === CONSENT_LEVELS.NONE) {
        return false;
      }

      // Essential level only allows essential cookies
      if (consentLevel === CONSENT_LEVELS.ESSENTIAL) {
        return false;
      }

      // ALL level allows everything
      return consentLevel === CONSENT_LEVELS.ALL;
    },
    [consentLevel]
  );

  /**
   * Check if consent has been given
   */
  const hasConsent = consentLevel && consentLevel !== CONSENT_LEVELS.NONE;

  return {
    // State
    consentLevel,
    isLoading,
    showBanner,
    hasConsent,

    // Actions
    acceptAll,
    acceptEssential,
    rejectAll,
    resetConsent,

    // Helpers
    isCookieAllowed,

    // Constants
    CONSENT_LEVELS,
  };
};

export default useCookieConsent;
